import { EqlFieldFiltersAutoComplete } from '@modules/common/components/EqlFiltersAutoComplete/EqlFieldFiltersAutoComplete';
import { EqlValueFiltersAutoComplete } from '@modules/common/components/EqlFiltersAutoComplete/EqlValueFiltersAutoComplete';
import { FieldIcon } from '@modules/common/components/FieldIcon';
import { SuggestionOption } from '@modules/common/components/MultiColumnAutoComplete/MultiColumnAutoCompleteExample/types';
import { ChevronLeftSharp } from '@mui/icons-material';
import {
  AutocompleteCloseReason,
  Box,
  Paper,
  Stack,
  TextFieldProps,
  Typography,
} from '@mui/material';
import {
  EqlField,
  EqlFieldValue,
  EqlFieldValueType,
  QueryBuilderGroupType,
} from 'generated/graphql';
import { SyntheticEvent, useState } from 'react';
import { EqlFilterGroupsAutoComplete } from './EqlFilterGroupsAutoComplete';

export interface EqlFiltersAutoCompleteProps {
  isOpen?: boolean;
  className?: string;
  onClose?: () => void;
  onChange: (params: {
    eqlField: EqlField;
    eqlFieldValue?: EqlFieldValue;
  }) => void;
  omitTextSearchOnEmptyTerm?: boolean;
  renderInput?: (
    textFieldProps: TextFieldProps,
    activeOption?: SuggestionOption,
  ) => React.ReactNode;
  openOnFocus?: boolean;
  onLoadingChange?: (isLoading: boolean) => void;
}

export function EqlFiltersAutoComplete({
  isOpen,
  className,
  onClose,
  onChange,
  omitTextSearchOnEmptyTerm,
  renderInput,
  openOnFocus = true,
  onLoadingChange,
}: EqlFiltersAutoCompleteProps) {
  const [inputValue, setInputValue] = useState('');
  const [shouldShowAllSuggestions, setShouldShowAllSuggestions] =
    useState(isOpen);

  const [activeViewMoreOption, setActiveViewMoreOption] =
    useState<SuggestionOption | null>(null);

  const onViewMore = (option: SuggestionOption) => {
    setActiveViewMoreOption(option);
  };

  const showAllSuggestions = () => {
    setActiveViewMoreOption(null);
    setShouldShowAllSuggestions(true);
  };

  const onInputChange = (_: SyntheticEvent, value: string) => {
    setInputValue(value);
    setShouldShowAllSuggestions(true);
  };

  const handleChange: EqlFiltersAutoCompleteProps['onChange'] = ({
    eqlField,
    eqlFieldValue,
  }) => {
    onChange({
      eqlField,
      eqlFieldValue,
    });
  };

  const handleAutoCompleteClose = (
    event: React.SyntheticEvent<Element, Event>,
    reason: AutocompleteCloseReason,
  ) => {
    if (
      reason === 'selectOption' &&
      activeViewMoreOption?.groupType === QueryBuilderGroupType.Field
    ) {
      return;
    }

    setShouldShowAllSuggestions(false);
    onClose?.();
  };

  if (!activeViewMoreOption?.groupType || !activeViewMoreOption.groupID) {
    return (
      <EqlFilterGroupsAutoComplete
        className={className}
        renderInput={renderInput}
        open={isOpen || shouldShowAllSuggestions}
        inputValue={inputValue}
        onInputChange={onInputChange}
        openOnFocus={openOnFocus}
        onChange={handleChange}
        onClose={handleAutoCompleteClose}
        onViewMore={onViewMore}
        omitTextSearchOnEmptyTerm={omitTextSearchOnEmptyTerm}
        onLoadingChange={onLoadingChange}
      />
    );
  }

  if (activeViewMoreOption.groupType === QueryBuilderGroupType.Field) {
    const getOptionDisplayName = (option: EqlField) => {
      if (option.valueType === EqlFieldValueType.None) {
        return option.displayName;
      }
      return `${option.displayName} is...`;
    };

    return (
      <EqlFieldFiltersAutoComplete
        open={isOpen}
        openOnFocus
        groupID={activeViewMoreOption.groupID}
        onClose={handleAutoCompleteClose}
        onChange={handleChange}
        inputVariant="outlined"
        inputValue={inputValue}
        onInputChange={onInputChange}
        groupBy={() => ''}
        ListboxProps={{ sx: { pt: 0 } }}
        getOptionDisplayName={(option) => getOptionDisplayName(option)}
        renderGroup={(params) => {
          return (
            <Box component="li" key={params.group}>
              <Paper
                elevation={1}
                onClick={showAllSuggestions}
                sx={{
                  pl: 1,
                  pr: 2,
                  py: 1,
                  cursor: 'pointer',
                  position: 'sticky',
                  top: 0,
                }}
              >
                <Typography color="text.secondary">
                  <ChevronLeftSharp fontSize="medium" /> Back to: All search
                  suggestions...
                </Typography>
              </Paper>
              <Box component="ul" p={0}>
                {params.children}
              </Box>
            </Box>
          );
        }}
      />
    );
  }

  if (activeViewMoreOption.groupType === QueryBuilderGroupType.Value) {
    return (
      <EqlValueFiltersAutoComplete
        open={isOpen}
        openOnFocus
        onClose={onClose}
        inputVariant="outlined"
        inputValue={inputValue}
        onInputChange={onInputChange}
        onChange={handleChange}
        eqlField={activeViewMoreOption.field}
        renderListHeader={() => {
          return (
            <Box onMouseDown={(event) => event.preventDefault()}>
              <Paper
                elevation={1}
                onClick={showAllSuggestions}
                sx={{ pl: 1, pr: 2, py: 1, cursor: 'pointer' }}
              >
                <Typography color="text.secondary">
                  <ChevronLeftSharp fontSize="medium" /> Back to: All search
                  suggestions...
                </Typography>
              </Paper>
              <Stack
                component={Paper}
                elevation={4}
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ px: 2, py: 1, boxShadow: 'none' }}
              >
                <FieldIcon
                  field={activeViewMoreOption.field.field}
                  iconURL={activeViewMoreOption.field.iconURL}
                  valueType={activeViewMoreOption.field.valueType}
                  objectType={activeViewMoreOption.field.objectType}
                />
                <Typography color="text.secondary">
                  {activeViewMoreOption.groupDisplayName} -{' '}
                  {activeViewMoreOption.field.displayName} is...
                </Typography>
              </Stack>
            </Box>
          );
        }}
      />
    );
  }

  return null;
}
